import React from 'react';
import { SaibaMais } from './SaibaMais';
import { Rocket, Check, Zap, TrendingUp, DollarSign, Shield, Star, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const BlingERP = ({ showLearnMore }) => {
  const benefits = [
    { icon: Zap, text: 'Automação de Processos', color: 'text-yellow-400' },
    { icon: TrendingUp, text: 'Gestão Financeira Completa', color: 'text-green-400' },
    { icon: Shield, text: 'Segurança e Backup', color: 'text-blue-400' },
    { icon: DollarSign, text: 'Controle de Vendas', color: 'text-emerald-400' }
  ];

  const handleTestClick = () => {
    window.open('https://www.bling.com.br/planos-e-precos/FERNANDOTECH', '_blank');
  };

  return (
    <section id="bling-erp" className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500 rounded-full filter blur-3xl animate-pulse delay-500 opacity-30"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/50 rounded-full animate-pulse">
            <Rocket className="w-5 h-5 text-blue-400" />
            <span className="text-blue-300 font-semibold text-sm uppercase tracking-wider">Parceiro Oficial</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
            Bling ERP - Teste Grátis
          </h2>
          <p className="text-xl md:text-2xl text-blue-200 mb-2 font-light">
            Sistema de Gestão Empresarial Completo
          </p>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Experimente <span className="text-blue-400 font-semibold">gratuitamente</span> o sistema que revoluciona a gestão do seu negócio
          </p>
        </div>

        {/* Conteúdo Principal */}
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Lado Esquerdo - Benefícios */}
            <div className="space-y-8">
              {/* Badge de Destaque */}
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/50 px-6 py-3 rounded-full">
                <Star className="w-5 h-5 text-yellow-400 animate-spin-slow" />
                <span className="text-yellow-300 font-bold text-lg">✨ Teste 100% Grátis</span>
              </div>

              {/* Título e Descrição */}
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Transforme seu Negócio com o Bling ERP
                </h3>
                <p className="text-slate-300 text-lg leading-relaxed">
                  O <span className="text-blue-400 font-semibold">Bling</span> é o sistema de gestão empresarial mais completo do mercado. 
                  Integre vendas, estoque, finanças e muito mais em uma única plataforma.
                </p>
              </div>

              {/* Lista de Benefícios */}
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-4 bg-slate-800/50 backdrop-blur-sm border border-blue-500/20 rounded-lg p-4 hover:border-blue-500/50 transition-all duration-300 hover:scale-105 hover:bg-slate-800/70"
                  >
                    <div className="bg-slate-900/80 p-3 rounded-lg">
                      <benefit.icon className={`w-6 h-6 ${benefit.color}`} />
                    </div>
                    <span className="text-white font-medium text-lg">{benefit.text}</span>
                    <Check className="w-5 h-5 text-green-400 ml-auto" />
                  </div>
                ))}
              </div>

              {/* Destaque Especial */}
              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <Shield className="w-8 h-8 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-bold text-xl mb-2">Por que escolher o Bling?</h4>
                    <ul className="text-slate-300 space-y-2">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>Sistema em nuvem - acesse de qualquer lugar</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>Emissão de Notas Fiscais integrada</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>Integração com marketplaces</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>Suporte especializado</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* CTA Principal */}
              <div className="space-y-4">
                <Button
                  size="lg"
                  onClick={handleTestClick}
                  className="w-full bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 hover:from-blue-700 hover:via-blue-600 hover:to-purple-700 text-white font-bold text-xl py-8 shadow-2xl shadow-blue-500/50 hover:shadow-blue-500/70 transition-all duration-300 hover:scale-105 rounded-xl group"
                >
                  <Rocket className="mr-3 w-6 h-6 group-hover:animate-bounce" />
                  Começar Teste Grátis Agora
                  <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </Button>
                <p className="text-center text-sm text-slate-400">
                  🎁 Sem necessidade de cartão de crédito • Teste completo por tempo limitado
                </p>
              </div>
            </div>

            {/* Lado Direito - Imagem Banner */}
            <div className="relative group overflow-hidden rounded-2xl">
              {/* Glow effect ao redor da imagem */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 rounded-2xl blur-2xl opacity-50 group-hover:opacity-75 transition-opacity duration-500 animate-pulse"></div>
              
              {/* Container da imagem */}
              <div 
                onClick={handleTestClick}
                className="relative bg-white rounded-2xl p-2 shadow-2xl cursor-pointer transition-all duration-500 hover:scale-105"
              >
                <img
                  src="./bling-erp-banner.png"
                  alt="Bling ERP - Sistema de Gestão"
                  className="w-full h-auto rounded-xl shadow-lg"
                />
                
                {/* Overlay com efeito hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-end justify-center p-8">
                  <div className="text-center space-y-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white font-bold text-2xl">Clique para Testar Grátis!</p>
                    <div className="flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
                      <Rocket className="w-5 h-5 text-white animate-bounce" />
                      <span className="text-white font-semibold">Começar Agora</span>
                    </div>
                  </div>
                </div>

                {/* Badge flutuante no canto */}
                <div className="absolute top-2 right-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold px-4 py-2 rounded-full shadow-xl transform rotate-12 group-hover:rotate-0 transition-transform duration-300 animate-bounce text-sm">
                  GRÁTIS!
                </div>
              </div>

              {/* Indicador de clique */}
              <div className="text-center mt-6">
                <p className="text-slate-400 text-sm animate-pulse">
                  👆 Clique na imagem para começar seu teste grátis
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Rodapé com garantia */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-3 bg-slate-800/50 border border-green-500/30 px-8 py-4 rounded-full">
            <Shield className="w-6 h-6 text-green-400" />
            <span className="text-white font-semibold text-lg">
              Fernando Tech - Parceiro Oficial Bling ERP
            </span>
            <Check className="w-6 h-6 text-green-400" />
          </div>
        </div>
      </div>

      {/* Estilos customizados */}
      <style jsx>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
      {showLearnMore && <SaibaMais to="/bling" label="Saiba mais sobre o Bling ERP" />}
    </section>
  );
};

export default BlingERP;
